import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useMediaQuery, useTheme } from "@mui/material";
import { useQuery, useQueryClient } from "react-query";

import { useAuthContext } from "../../../context/AuthContext";
import { useNotificationsOptional } from "../../../context/NotificationContext";
import { useConversations } from "../../../hooks/useConversations";
import { useConversationDetails } from "../../../hooks/useConversationDetails";
import { useMessages } from "../../../hooks/useMessages";
import { useRealtimeMessages } from "../../../hooks/useRealtimeMessages";
import {
  getConversationMessagesById,
  deleteConversationMessage,
  fetchData,
  getPersonalWhatsappChatsList,
  getPersonalWhatsappChatMessages,
  getPersonalChatKeyFromSyntheticConversationId,
  deletePersonalWhatsappChat,
} from "../../../api/client";
import { useCreateConversation } from "../../../hooks/useCreateConversation"; 
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import { getConversationInsights } from "../../../api/client";
import { enrichMessageLocationFromApi } from "../../../utils/enrichMessageLocation";
import { deliveryStatusFromPersonalWhatsappStatus } from "../../../components/MessageHistory/messageDeliveryStatus";
import { toE164Digits } from "../../../utils/phone";

// ✅ Função auxiliar para padronizar o status
const getStatusLabelFromCode = (status) => {
  const statusMap = {
    0: "Pendente",
    1: "Em Atendimento",
    2: "Concluída",
    3: "Contato Futuro",
    4: "Perdida",
    5: "Cancelada",
  };

  if (typeof status === "string" && !isNaN(Number(status))) {
    status = Number(status);
  }

  return statusMap[status] ?? "Desconhecido";
};

const CHAT_FILTERS_STORAGE_KEY = "xpro_chat_filters";
const CHAT_SEARCH_STORAGE_KEY = "xpro_chat_search";
const CHAT_LAST_SEEN_CLIENT_AT_KEY = "xpro_chat_last_seen_client_at";

function mapPersonalRedisMessageToUi(m, userData) {
  if (!m) return null;
  const uid = userData?.id;
  const isOut = m.direction === "out";
  const ts = m.ts != null ? Number(m.ts) : Date.now();
  const iso = new Date(ts).toISOString();
  const arquivoUrl = m.arquivo || null;
  const row = {
    id: m.id,
    text: m.text || "",
    mensagem: m.text || "",
    tipo: 3,
    sender: isOut ? "operator" : "client",
    senderType: isOut ? "agent" : "client",
    id_usuario: isOut ? uid : 0,
    senderId: isOut ? uid : undefined,
    timestamp: iso,
    createdAt: iso,
    lastUpdate: iso,
    data_cadastro: iso.replace("T", " ").slice(0, 19),
    ts,
    imagem: m.imagem || null,
    image: m.imagem || null,
    video: m.video || null,
    audio: m.audio || null,
    arquivo: arquivoUrl,
  };
  if (arquivoUrl) {
    try {
      const u = new URL(String(arquivoUrl));
      const base = u.pathname.split("/").pop() || "documento";
      row.document = { url: arquivoUrl, name: decodeURIComponent(base) };
    } catch {
      row.document = { url: arquivoUrl, name: "documento" };
    }
  }
  if (m.imagem) row.type = "image";
  else if (m.audio) row.type = "audio";
  else if (m.video) row.type = "video";
  else if (m.arquivo) row.type = "document";
  else row.type = "text";
  if (isOut) {
    const nm = String(userData?.nome || userData?.name || "").trim();
    if (nm) row.senderName = nm;
    row.deliveryStatus = deliveryStatusFromPersonalWhatsappStatus(m.statusWhatsapp);
  }
  if (m.waMessageId != null && String(m.waMessageId).trim()) {
    row.waMessageId = String(m.waMessageId).trim();
  }
  return enrichMessageLocationFromApi(row);
}

/**
 * Otimista (temp- / status sending) já coberta por linha vinda do Redis (mesmo waMessageId ou mesmo texto outbound recente).
 */
function personalPendingSupersededByServerList(serverUiMessages, pendingKey, pendingMsg, userData) {
  const isPending =
    String(pendingKey).startsWith("temp-") ||
    pendingMsg?.status === "sending" ||
    pendingMsg?.status === "error";
  if (!isPending) return false;
  const wa = String(pendingMsg?.waMessageId || pendingMsg?.wa_message_id || "").trim();
  if (wa && serverUiMessages.some((s) => String(s?.waMessageId || s?.wa_message_id || "").trim() === wa)) {
    return true;
  }
  const txt = String(pendingMsg?.text || "").trim();
  if (!txt) return false;
  const uid = userData?.id;
  const isAgentOut =
    pendingMsg?.senderType === "agent" ||
    pendingMsg?.sender === "operator" ||
    (uid != null && Number(pendingMsg?.id_usuario) === Number(uid));
  if (!isAgentOut) return false;
  const msgTs =
    Number(pendingMsg?.ts) ||
    new Date(pendingMsg?.timestamp || pendingMsg?.createdAt || pendingMsg?.lastUpdate || 0).getTime() ||
    0;
  return serverUiMessages.some((s) => {
    const outbound =
      s?.senderType === "agent" ||
      s?.sender === "operator" ||
      (uid != null && Number(s?.id_usuario) === Number(uid));
    if (!outbound) return false;
    if (String(s?.text || "").trim() !== txt) return false;
    const sTs =
      Number(s?.ts) ||
      new Date(s?.timestamp || s?.createdAt || s?.lastUpdate || 0).getTime() ||
      0;
    // Eco do Redis costuma chegar poucos s após o otimista; evita colidir com outro envio igual mais tarde.
    return sTs >= msgTs - 5000 && sTs <= msgTs + 120_000;
  });
}

function stripHtmlSimple(s) {
  if (!s || typeof s !== "string") return "";
  return s.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

/**
 * Remove mensagem otimista temp-* quando o servidor já devolveu o mesmo envio (socket/polling).
 * Evita duplicação visual enquanto status === "sending" (comum em anotações internas).
 */
function regularConversationPendingSupersededByServer(serverMessages, pendingKey, pendingMsg, userData) {
  const isPending =
    String(pendingKey).startsWith("temp-") ||
    pendingMsg?.status === "sending" ||
    pendingMsg?.status === "error";
  if (!isPending) return false;

  const txt = stripHtmlSimple(String(pendingMsg?.text || pendingMsg?.mensagem || "").trim());
  if (!txt) return false;

  const uid = userData?.id;
  const isAgentOut =
    pendingMsg?.senderType === "agent" ||
    pendingMsg?.sender === "operator" ||
    pendingMsg?.sender === "Agent" ||
    (uid != null && Number(pendingMsg?.senderId) === Number(uid));

  if (!isAgentOut) return false;

  const msgTs =
    Number(pendingMsg?.ts) ||
    new Date(pendingMsg?.timestamp || pendingMsg?.createdAt || pendingMsg?.lastUpdate || 0).getTime() ||
    0;

  return serverMessages.some((s) => {
    const sTxt = stripHtmlSimple(String(s?.text || s?.mensagem || "").trim());
    if (sTxt !== txt) return false;

    const outbound =
      s?.senderType === "agent" ||
      s?.sender === "operator" ||
      s?.sender === "Agent" ||
      (uid != null && Number(s?.id_usuario) === Number(uid));

    if (!outbound) return false;

    const sTs =
      Number(s?.ts) ||
      new Date(s?.timestamp || s?.createdAt || s?.lastUpdate || s?.updatedAt || 0).getTime() ||
      0;

    if (!msgTs || !sTs) return true;

    return sTs >= msgTs - 5000 && sTs <= msgTs + 120_000;
  });
}

/** Foco do atendente ao logar: minhas solicitações pendentes e em atendimento ("Meu dia") */
const DEFAULT_FILTERS = [
  "Minhas Solicitações",
  "Pendente",
  "Em Atendimento",
];

function loadPersistedFilters() {
  try {
    const raw = localStorage.getItem(CHAT_FILTERS_STORAGE_KEY);
    if (raw == null) return DEFAULT_FILTERS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_FILTERS;
    const valid = parsed.filter((f) => typeof f === "string");
    return valid.length ? valid : DEFAULT_FILTERS;
  } catch {
    return DEFAULT_FILTERS;
  }
}

function loadPersistedSearch() {
  try {
    const s = localStorage.getItem(CHAT_SEARCH_STORAGE_KEY);
    return typeof s === "string" ? s : "";
  } catch {
    return "";
  }
}

function loadLastSeenClientAt() {
  try {
    const raw = localStorage.getItem(CHAT_LAST_SEEN_CLIENT_AT_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

/** Corpo lançado por `fetchData` em erro: costuma preservar `code` e `data` do backend. */
function getApiErrorMeta(err) {
  const code =
    (typeof err?.code === "string" && err.code) ||
    (typeof err?.data?.code === "string" && err.data.code) ||
    null;
  const duplicatesFromData = err?.data?.duplicates;
  const duplicates =
    Array.isArray(duplicatesFromData)
      ? duplicatesFromData
      : Array.isArray(err?.duplicates)
        ? err.duplicates
        : [];
  const message =
    (typeof err?.message === "string" && err.message) ||
    (typeof err?.data?.message === "string" && err.data.message) ||
    null;
  return { code, duplicates, message };
}

export default function useChatState() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const { userData } = useAuthContext();
  const notificationsCtx = useNotificationsOptional();
  const queryClient = useQueryClient();

  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messagesMap, setMessagesMap] = useState(new Map());
  const messages = Array.from(messagesMap.values());
  const selectedConversationIdRef = useRef(selectedConversationId);
  useEffect(() => {
    selectedConversationIdRef.current = selectedConversationId;
  }, [selectedConversationId]);

  const onSocketActivity = useCallback(
    (evt) => {
      const isConversationSelected =
        selectedConversationId != null &&
        String(selectedConversationId) === String(evt.conversationId);
      notificationsCtx?.dispatchRealtimeSocket?.({
        ...evt,
        isConversationSelected,
      });
    },
    [notificationsCtx, selectedConversationId]
  );
  const { sendMessageMutation } = useMessages();

  const { createConversation, isCreating, errorCreating } = useCreateConversation();

  const [aiInsights, setAiInsights] = useState(null);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);
  const [aiInsightsError, setAiInsightsError] = useState(null);
  const [aiInsightsConversationId, setAiInsightsConversationId] = useState(null);

  const [initialLoading, setInitialLoading] = useState(true);
  const [showConversationList, setShowConversationList] = useState(true);
  const [showMessageHistory, setShowMessageHistory] = useState(!isMobile);
  const [showConversationDetails, setShowConversationDetails] = useState(false);
  const [searchTerm, setSearchTerm] = useState(loadPersistedSearch);
  const [filters, setFilters] = useState(loadPersistedFilters);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const [requestTotal, setRequestTotal] = useState(false);
  const hasRequestedTotal = useRef(false);
  const [lastSeenClientAtMap, setLastSeenClientAtMap] = useState(loadLastSeenClientAt);
  const [messagesPageState, setMessagesPageState] = useState({
    hasMore: false,
    lastMessageId: null,
    isInitialLoaded: false,
    isLoadingMore: false,
  });
  /** Falha ao carregar/sincronizar histórico (ex.: duplicidade de cliente no backend). */
  const [messagesSyncError, setMessagesSyncError] = useState(null);

  // Persistir filtros e busca para manter após reload
  useEffect(() => {
    try {
      localStorage.setItem(CHAT_FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch (e) {
      console.warn("Falha ao salvar filtros do chat no localStorage", e);
    }
  }, [filters]);

  useEffect(() => {
    try {
      localStorage.setItem(CHAT_SEARCH_STORAGE_KEY, searchTerm);
    } catch (e) {
      console.warn("Falha ao salvar busca do chat no localStorage", e);
    }
  }, [searchTerm]);

  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500);
  const debouncedFilters = useDebouncedValue(filters, 400);
  // Pipeline afeta tipo_scope na API: usar estado atual do chip, não o valor debounced,
  // para não pedir relacionamento (t.tipo=1) por ~400ms após ligar o filtro.
  const isSalesPipelineSelected = (filters || []).includes("Pipeline de Vendas");
  const debouncedFiltersForApi = (debouncedFilters || []).filter(
    (f) => f !== "Pipeline de Vendas"
  );

  const personalFilterOn = (filters || []).includes("WhatsApp pessoal");

  const {
    data: conversationDetails,
    isLoading: isLoadingDetails,
    refetch: refetchConversationDetails,
  } = useConversationDetails(selectedConversationId);

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    refetch,
  } = useConversations(
    debouncedSearchTerm,
    debouncedFiltersForApi,
    (() => {
      const quemFilters = [
        "Minhas Solicitações",
        "Outros Usuários",
        "Fila",
        "ChatBots",
        "Agentes de IA",
      ];
      const selectedQuem = (debouncedFiltersForApi || []).filter((f) =>
        quemFilters.includes(f)
      );
      const multipleQuem = selectedQuem.length > 1;

      const tipoFilters = ["ChatBots", "Agentes de IA", "Fila"];
      const selectedTipo = (debouncedFiltersForApi || []).filter((f) =>
        tipoFilters.includes(f)
      );
      const singleTipo = selectedTipo.length === 1 ? selectedTipo[0] : null;
      const tipoMap = {
        ChatBots: "chatbots",
        "Agentes de IA": "agentes",
        Fila: "fila",
      };
      // Com 2+ opções em Quem, a lista usa OR no cliente; tipo_responsavel na API seria AND e esconderia resultados.
      const tipoResponsavel =
        !multipleQuem && singleTipo && tipoMap[singleTipo]
          ? tipoMap[singleTipo]
          : null;

      return {
        tipo_scope: isSalesPipelineSelected ? "todos" : "relacionamento",
        default_date_window_days: 30,
        ...(tipoResponsavel ? { tipo_responsavel: tipoResponsavel } : {}),
      };
    })(),
    requestTotal
  );

  // Pedir total no banco só após a 1ª carga (evita COUNT pesado no primeiro request)
  useEffect(() => {
    if (data?.pages?.length > 0 && !hasRequestedTotal.current) {
      hasRequestedTotal.current = true;
      setRequestTotal(true);
    }
  }, [data?.pages?.length]);

  const conversationsFromApi = data?.pages?.flatMap((page) => page.list) ?? [];
  const conversationsWithUnread = conversationsFromApi.map((c) => {
    const lastClientAt = c.lastClientMessageAt;
    const seenAt = lastSeenClientAtMap?.[c.id] ?? null;
    const hasNewClientReply =
      lastClientAt &&
      (!seenAt || new Date(lastClientAt).getTime() > new Date(seenAt).getTime());

    return {
      ...c,
      unreadClient: hasNewClientReply ? 1 : 0,
    };
  });
  const totalInDatabase = data?.pages?.[0]?.totalInDatabase;

  const { data: meRoot } = useQuery(["me"], () => fetchData("/users/me"), {
    staleTime: 20000,
  });
  const wpPersonalEnabled = Boolean(meRoot?.data?.whatsapp_personal?.enabled);

  const { data: personalChatsRaw = [] } = useQuery(
    ["personal-whatsapp-chats", personalFilterOn, wpPersonalEnabled],
    () => getPersonalWhatsappChatsList(),
    {
      enabled: personalFilterOn && wpPersonalEnabled,
      refetchInterval: personalFilterOn && wpPersonalEnabled ? 10000 : false,
    }
  );

  const personalAsConversations = useMemo(() => {
    if (!personalFilterOn || !wpPersonalEnabled || !Array.isArray(personalChatsRaw)) return [];
    return personalChatsRaw.map((chat) => ({
      id: `wa-personal:${encodeURIComponent(chat.chatKey)}`,
      kind: "personal",
      personalChatKey: chat.chatKey,
      name: (chat.peerName && String(chat.peerName).trim()) || chat.peerPhone || chat.chatKey,
      nome: (chat.peerName && String(chat.peerName).trim()) || chat.peerPhone || chat.chatKey,
      profile_picture: chat.peerAvatar || null,
      lastMessage: chat.lastMessage,
      timestamp:
        chat.timestamp != null
          ? new Date(Number(chat.timestamp)).toISOString()
          : new Date().toISOString(),
      userId: userData?.id,
      assignedTo: userData?.nome,
      userName: userData?.nome,
      status: 1,
      statusLabel: "Em Atendimento",
      channel: "WhatsApp pessoal",
      channelLabel: "WhatsApp pessoal",
      channelType: "whatsapp",
      unreadClient: Number(chat.unreadClient) || 0,
      celular: chat.peerPhone
        ? toE164Digits(String(chat.peerPhone).replace(/\D/g, ""), "BR") ||
          String(chat.peerPhone).replace(/\D/g, "")
        : "",
    }));
  }, [personalFilterOn, wpPersonalEnabled, personalChatsRaw, userData]);

  const conversations = useMemo(() => {
    const merged = [...personalAsConversations, ...conversationsWithUnread];
    return merged.sort((a, b) => {
      const ta = new Date(a.timestamp || 0).getTime();
      const tb = new Date(b.timestamp || 0).getTime();
      if (tb !== ta) return tb - ta;
      return String(b.id).localeCompare(String(a.id));
    });
  }, [personalAsConversations, conversationsWithUnread]);

  // Throttle para evitar spam de refetch quando chegam muitos eventos socket.
  const lastListRefreshAtRef = useRef(0);
  const listRefreshCooldownMs = 2500;
  const listRefreshRefetch = useCallback(() => {
    const now = Date.now();
    if (now - lastListRefreshAtRef.current < listRefreshCooldownMs) return;
    lastListRefreshAtRef.current = now;
    if (typeof refetch === "function") refetch();
  }, [refetch]);

  // Reconcile do histórico usando o endpoint paginado correto.
  // Mantém mensagens já carregadas (merge idempotente) para não quebrar paginação/scroll.
  const messagesSyncInFlightRef = useRef(false);
  const syncLatestConversationMessagesById = useCallback(
    async (conversationId, { limit = 50 } = {}) => {
    if (!conversationId) return;
    const pKey = getPersonalChatKeyFromSyntheticConversationId(conversationId);
    if (pKey) {
      if (messagesSyncInFlightRef.current) return;
      messagesSyncInFlightRef.current = true;
      try {
        const payload = await getPersonalWhatsappChatMessages(pKey);
        const list = Array.isArray(payload?.messages) ? payload.messages : [];
        setMessagesMap((prev) => {
          const serverUi = list
            .map((m) => mapPersonalRedisMessageToUi(m, userData))
            .filter((u) => u?.id != null);
          const next = new Map(prev);
          for (const ui of serverUi) next.set(ui.id, ui);
          for (const [key, msg] of [...next.entries()]) {
            if (personalPendingSupersededByServerList(serverUi, key, msg, userData)) {
              next.delete(key);
            }
          }
          return next;
        });
      } catch (err) {
        console.error("Erro ao sincronizar mensagens (WhatsApp pessoal):", err);
      } finally {
        messagesSyncInFlightRef.current = false;
      }
      return;
    }
    if (messagesSyncInFlightRef.current) return;
    messagesSyncInFlightRef.current = true;
    try {
      const payload = await getConversationMessagesById(String(conversationId), {
        limit,
        returnRaw: true,
      });

      const list = Array.isArray(payload?.messages)
        ? payload.messages
        : Array.isArray(payload?.data)
          ? payload.data
          : [];

      if (String(selectedConversationIdRef.current) === String(conversationId)) {
        setMessagesSyncError(null);
      }

      setMessagesMap((prev) => {
        const next = new Map(prev);

        const mergeKeepingLocalMedia = (prevMsg, incoming) => {
          if (!prevMsg) return incoming;
          const merged = { ...prevMsg, ...incoming };
          const keepIfMissing = ["image", "audio", "video", "file", "document", "location"];
          keepIfMissing.forEach((k) => {
            if (
              (merged[k] === null || merged[k] === undefined || merged[k] === "") &&
              prevMsg[k]
            ) {
              merged[k] = prevMsg[k];
            }
          });
          return merged;
        };

        for (const msg of list) {
          if (!msg || msg.id == null) continue;
          const existing = next.get(msg.id);
          next.set(msg.id, mergeKeepingLocalMedia(existing, enrichMessageLocationFromApi(msg)));
        }

        for (const [key, msg] of [...next.entries()]) {
          if (regularConversationPendingSupersededByServer(list, key, msg, userData)) {
            next.delete(key);
          }
        }

        return next;
      });
    } catch (err) {
      console.error("Erro ao sincronizar mensagens (realtime/polling):", err);
      if (String(selectedConversationIdRef.current) !== String(conversationId)) return;
      const meta = getApiErrorMeta(err);
      setMessagesSyncError((prev) => {
        if (meta.code === "errors.clients.duplicate" && meta.duplicates.length) {
          return {
            code: meta.code,
            message: meta.message || prev?.message || "Não foi possível carregar o histórico.",
            duplicates: meta.duplicates,
          };
        }
        if (prev?.code === "errors.clients.duplicate") return prev;
        return {
          code: meta.code,
          message: meta.message || "Não foi possível atualizar o histórico.",
          duplicates: [],
        };
      });
    } finally {
      messagesSyncInFlightRef.current = false;
    }
    },
    [userData]
  );

  const markClientMessagesAsSeen = (conversation) => {
    if (!conversation) return;
    const convId = conversation.id;
    const lastClientAt = conversation.lastClientMessageAt;
    if (!convId || !lastClientAt) return;

    setLastSeenClientAtMap((prev) => {
      const next = {
        ...prev,
        [convId]: lastClientAt,
      };
      try {
        localStorage.setItem(CHAT_LAST_SEEN_CLIENT_AT_KEY, JSON.stringify(next));
      } catch (e) {
        console.warn("Falha ao salvar lastSeenClientAt no localStorage", e);
      }
      return next;
    });
  };

  const loadAiInsights = async (conversationIdParam) => {
    const conversationIdToUse = conversationIdParam || selectedConversationId;
    if (!conversationIdToUse) return;
    if (getPersonalChatKeyFromSyntheticConversationId(conversationIdToUse)) return;

    // Se já temos insights para esta conversa específica, não recarrega
    if (
      aiInsights &&
      aiInsightsConversationId != null &&
      String(aiInsightsConversationId) === String(conversationIdToUse)
    ) {
      return;
    }

    const requestConversationId = String(conversationIdToUse);

    try {
      setIsLoadingInsights(true);
      setAiInsightsError(null);

      const data = await getConversationInsights(requestConversationId);

      // Se, quando a resposta chegou, já mudamos de conversa, ignora este resultado
      if (String(selectedConversationId) !== String(requestConversationId)) {
        return;
      }

      setAiInsights(data || null);
      setAiInsightsConversationId(requestConversationId);

      // também grava no objeto da conversa atual para reaproveitar ao reabrir o acordeão
      setSelectedConversation((prev) => {
        if (!prev || prev.id !== requestConversationId) return prev;
        return { ...prev, aiInsights: data };
      });
    } catch (err) {
      console.error("Erro ao carregar insights da conversa:", err);

      // só aplica o erro se ainda estivermos na mesma conversa
      if (String(selectedConversationId) === String(requestConversationId)) {
        setAiInsightsError(err?.message || "Erro ao carregar insights da conversa.");
      }
    } finally {
      if (String(selectedConversationId) === String(requestConversationId)) {
        setIsLoadingInsights(false);
      }
    }
  };

  useRealtimeMessages({
    selectedConversationId,
    refetchConversationDetails,
    refetchConversations: listRefreshRefetch,
    refreshConversationMessages: syncLatestConversationMessagesById,
    onSocketActivity,
  });

  useEffect(() => {
    if (!conversationDetails?.data) return;

    const d = conversationDetails.data; // payload do endpoint /conversations/:id
    // Backend pode enviar agente no objeto principal ou aninhado (ex.: request, solicitation)
    const req = d.request ?? d.solicitation ?? d.demand ?? d;

    const rawTypeType = d.typeType ?? req.typeType ?? null;

    const agentId =
      d.agentId ?? d.id_agente ?? req.agentId ?? req.id_agente ?? null;
    const agentName =
      d.agentName ?? d.agent_name ?? req.agentName ?? req.agent_name ?? null;
    const client = d.client ?? null;
    // normalização única para a UI:
    const normalized = {
      id: d.id,
      status: d.status,
      statusLabel: getStatusLabelFromCode(d.status),
      projectId: d.projectId ?? req.projectId ?? null,
      channelId: d.channelId ?? req.channelId ?? null,
      typeId: d.typeId ?? req.typeId ?? null,
      typeType: rawTypeType,
      subtype1: d.subtype1 ?? req.subtype1 ?? null,
      subtype2: d.subtype2 ?? req.subtype2 ?? null,
      subtype3: d.subtype3 ?? req.subtype3 ?? null,
      userId: d.userId ?? req.userId ?? null,
      userName: d.userName ?? req.userName ?? null,
      agentId,
      agentName,
      potential: d.potential ?? req.potential ?? null,
      nextContactAt: d.nextContactAt ?? req.nextContactAt ?? null,
      nf: d.nf ?? req.nf ?? null,

      // mapeia “cliente” para os nomes esperados pelos componentes
      clientId: d.clientId ?? null,
      nome: client?.name ?? "",
      email: client?.email ?? "",
      celular: client?.phone ?? "",
      cep: client?.zip ?? "",
      endereco: client?.address ?? "",
      numero: client?.addressNumber ?? "",
      complemento: client?.addressComplement ?? "",
      bairro: client?.neighborhood ?? "",
      cidade: client?.city ?? client?.locality ?? null,
      uf: client?.state ?? null,
      clientPotential: client?.potential ?? null,
      // Se o cliente atual não vier com grupo/tags, NÃO herdar do anterior.
      id_grupo: client?.id_grupo ?? null,
      clientTags: client?.tags ?? "",
      tags: d.tags ?? null,

      // channel string (ex.: “Ecommerce”)
      channel: d.channel ?? null,
      channelType: d.channelType ?? d.channel_type ?? null,

      // messages para o histórico (carregadas separadamente via /conversations/:id/messages)
      messages: Array.isArray(d.messages) ? d.messages : [],
      products: Array.isArray(d.products) ? d.products : [],
      history: Array.isArray(d.history) ? d.history : [],
      aiInsights,
    };

    // Usa sempre `prev` para não "colar" dados de outra conversa.
    setSelectedConversation((prev) => {
      const prevId = prev?.id != null ? String(prev.id) : null;
      const nextId = normalized?.id != null ? String(normalized.id) : null;
      if (prevId && nextId && prevId !== nextId) {
        return { ...normalized };
      }
      return { ...(prev || {}), ...normalized };
    });
    setIsLoadingConversation(false);
  }, [conversationDetails]);

  useEffect(() => {
    const isChatRoute =
      location.pathname === "/chat" || location.pathname.startsWith("/chat/");
    if (!isChatRoute) return;

    if (id) {
      setSelectedConversationId(id);
      // Mobile: abrir pela URL (ex.: notificação → /chat/:id) precisa espelhar o mesmo
      // layout que um toque na lista; senão o histórico fica oculto com showMessageHistory=false.
      if (isMobile) {
        setShowMessageHistory(true);
        setShowConversationList(false);
        setShowConversationDetails(false);
      }
    }
  }, [id, location.pathname, isMobile]);

  // Ao trocar de conversa: limpa insights e marca carregamento. Não zera `selectedConversation`
  // se já reflete o mesmo id (ex.: clique na lista acabou de preencher com o item da fila) —
  // senão há um frame “vazio” até o GET /conversations/:id concluir.
  useEffect(() => {
    if (!selectedConversationId) return;
    setIsLoadingConversation(true);
    setSelectedConversation((prev) =>
      prev != null && String(prev.id) === String(selectedConversationId) ? prev : null
    );
    setAiInsights(null);
    setAiInsightsError(null);
    setAiInsightsConversationId(null);
  }, [selectedConversationId]);

  // Carrega a primeira página de mensagens sempre que uma nova conversa é selecionada
  useEffect(() => {
    const loadInitialMessages = async () => {
      if (!selectedConversationId) return;
      setMessagesSyncError(null);
      setMessagesPageState((prev) => ({
        ...prev,
        isInitialLoaded: false,
        isLoadingMore: true,
      }));
      try {
        const pKey = getPersonalChatKeyFromSyntheticConversationId(selectedConversationId);
        if (pKey) {
          const payload = await getPersonalWhatsappChatMessages(pKey);
          const list = Array.isArray(payload?.messages) ? payload.messages : [];
          setMessagesMap((prev) => {
            const serverUi = list
              .map((m) => mapPersonalRedisMessageToUi(m, userData))
              .filter((u) => u?.id != null);
            const next = new Map();
            for (const ui of serverUi) next.set(ui.id, ui);
            prev.forEach((msg, key) => {
              const isPending =
                String(key).startsWith("temp-") ||
                msg.status === "sending" ||
                msg.status === "error";
              if (!isPending) return;
              if (personalPendingSupersededByServerList(serverUi, key, msg, userData)) return;
              if (!next.has(key)) next.set(key, msg);
            });
            return next;
          });
          setMessagesPageState({
            hasMore: false,
            lastMessageId: null,
            isInitialLoaded: true,
            isLoadingMore: false,
          });
          setIsLoadingConversation(false);
          setMessagesSyncError(null);
          return;
        }

        const payload = await getConversationMessagesById(selectedConversationId, {
          limit: 50,
          returnRaw: true,
        });
        const list = Array.isArray(payload.messages)
          ? payload.messages
          : Array.isArray(payload.data)
            ? payload.data
            : [];

        setMessagesMap((prev) => {
          const next = new Map();
          for (const msg of list) {
            next.set(msg.id, enrichMessageLocationFromApi(msg));
          }
          // preserva mensagens locais pendentes (ex.: envios em andamento)
          prev.forEach((msg, key) => {
            const isPending =
              String(key).startsWith("temp-") ||
              msg.status === "sending" ||
              msg.status === "error";
            if (!isPending) return;
            if (regularConversationPendingSupersededByServer(list, key, msg, userData)) return;
            if (!next.has(key)) next.set(key, msg);
          });
          return next;
        });

        const pagination = payload.pagination || {};
        setMessagesPageState({
          hasMore: Boolean(pagination.hasMore),
          lastMessageId: pagination.lastMessageId ?? null,
          isInitialLoaded: true,
          isLoadingMore: false,
        });
        setMessagesSyncError(null);
      } catch (err) {
        console.error("Erro ao carregar mensagens da conversa:", err);
        const meta = getApiErrorMeta(err);
        setMessagesSyncError({
          code: meta.code,
          message:
            meta.message ||
            "Não foi possível carregar o histórico desta conversa. Tente novamente.",
          duplicates: meta.duplicates || [],
        });
        setMessagesPageState((prev) => ({
          ...prev,
          isLoadingMore: false,
          isInitialLoaded: true,
        }));
        setIsLoadingConversation(false);
      }
    };

    // ao trocar de conversa, limpa mensagens anteriores antes de carregar
    setMessagesMap(new Map());
    if (selectedConversationId) {
      loadInitialMessages();
    }
  }, [selectedConversationId, userData]);

  // Reconciliação por polling (rede de segurança quando o socket não chega).
  // Rodamos apenas quando a aba está visível.
  const pagesLenRef = useRef(data?.pages?.length ?? 0);
  useEffect(() => {
    pagesLenRef.current = data?.pages?.length ?? 0;
  }, [data?.pages?.length]);

  useEffect(() => {
    const isChatRoute =
      location.pathname === "/chat" || location.pathname.startsWith("/chat/");
    if (!isChatRoute) return;

    if (typeof document === "undefined") return;

    const runHistorySync = () => {
      if (document.visibilityState !== "visible") return;
      const activeId = selectedConversationIdRef.current;
      if (activeId) syncLatestConversationMessagesById(activeId, { limit: 50 });
    };

    const runListSync = () => {
      if (document.visibilityState !== "visible") return;
      // Evita churn na paginação infinita: só refaz quando normalmente temos 1 página.
      if ((pagesLenRef.current ?? 0) <= 1) listRefreshRefetch();
    };

    const syncNow = () => {
      runHistorySync();
      runListSync();
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") syncNow();
    };

    window.addEventListener("focus", syncNow);
    document.addEventListener("visibilitychange", handleVisibility);

    const historyIntervalMs = 12000;
    const listIntervalMs = 24000;

    const historyTimer = setInterval(runHistorySync, historyIntervalMs);
    const listTimer = setInterval(runListSync, listIntervalMs);

    // Um sync inicial ao entrar/voltar para a rota de chat.
    syncNow();

    return () => {
      window.removeEventListener("focus", syncNow);
      document.removeEventListener("visibilitychange", handleVisibility);
      clearInterval(historyTimer);
      clearInterval(listTimer);
    };
  }, [
    location.pathname,
    listRefreshRefetch,
    syncLatestConversationMessagesById,
  ]);

  useEffect(() => {
    if (!isLoading) setInitialLoading(false);
  }, [isLoading]);

  useEffect(() => {
    if (getPersonalChatKeyFromSyntheticConversationId(selectedConversationId)) {
      setShowConversationDetails(false);
    }
  }, [selectedConversationId]);

  const deletePersonalConversation = useCallback(async () => {
    const pKey = getPersonalChatKeyFromSyntheticConversationId(selectedConversationId);
    if (!pKey) throw new Error("chatKey ausente");
    await deletePersonalWhatsappChat(pKey);
    await queryClient.invalidateQueries(["personal-whatsapp-chats"]);
    setSelectedConversation(null);
    setSelectedConversationId(null);
    setMessagesMap(new Map());
    setAiInsights(null);
    setAiInsightsError(null);
    setAiInsightsConversationId(null);
    setMessagesPageState({
      hasMore: false,
      lastMessageId: null,
      isInitialLoaded: false,
      isLoadingMore: false,
    });
    setMessagesSyncError(null);
    setIsLoadingConversation(false);
    navigate("/chat");
    if (isMobile) {
      setShowConversationList(true);
      setShowMessageHistory(false);
    }
    setShowConversationDetails(false);
  }, [selectedConversationId, queryClient, navigate, isMobile]);

  const handleSelectConversation = (conversation) => {
    if (!conversation?.id) return;

    const nextId = String(conversation.id);
    const isSameConversation =
      selectedConversationId != null &&
      String(selectedConversationId) === nextId;

    // Sempre atualiza dados básicos e marca mensagens do cliente como lidas
    setSelectedConversation((prev) => {
      const base = { ...(prev || {}), ...conversation };
      const hasClientTags =
        conversation?.clientTags != null ||
        conversation?.client?.tags != null;
      const hasClientGroup =
        conversation?.id_grupo != null ||
        conversation?.client?.id_grupo != null;
      const hasConversationTags = conversation?.tags != null;

      return {
        ...base,
        // Defesa: não deixar tags/grupo “vazarem” ao trocar de conversa
        ...(hasClientTags ? {} : { clientTags: "" }),
        ...(hasClientGroup ? {} : { id_grupo: null }),
        ...(hasConversationTags ? {} : { tags: null }),
      };
    });
    markClientMessagesAsSeen(conversation);

    const isPersonalWa =
      conversation.kind === "personal" ||
      String(conversation.id || "").startsWith("wa-personal:");
    if (isPersonalWa) {
      setShowConversationDetails(false);
    }

    if (isMobile) {
      setShowMessageHistory(true);
      setShowConversationList(false);
      setShowConversationDetails(false);
    }

    // Se for a mesma conversa, apenas garante a navegação/visibilidade,
    // sem forçar estado de "carregando" infinito.
    if (isSameConversation) {
      setMessagesSyncError(null);
      setIsLoadingConversation(false);
      navigate(`/chat/${encodeURIComponent(nextId)}`);
      return;
    }

    // Nova conversa: dispara carregamento de detalhes e mensagens
    setMessagesSyncError(null);
    setIsLoadingConversation(true);
    setAiInsights(null);
    setAiInsightsError(null);
    setAiInsightsConversationId(null);
    setSelectedConversationId(nextId);
    navigate(`/chat/${encodeURIComponent(nextId)}`);
  };

  const reloadConversationMessages = useCallback(async () => {
    const cid = selectedConversationId;
    if (!cid) return;
    setMessagesSyncError(null);
    setMessagesPageState((prev) => ({
      ...prev,
      isInitialLoaded: false,
      isLoadingMore: true,
    }));
    try {
      const pKey = getPersonalChatKeyFromSyntheticConversationId(cid);
      if (pKey) {
        const payload = await getPersonalWhatsappChatMessages(pKey);
        const list = Array.isArray(payload?.messages) ? payload.messages : [];
        setMessagesMap((prev) => {
          const serverUi = list
            .map((m) => mapPersonalRedisMessageToUi(m, userData))
            .filter((u) => u?.id != null);
          const next = new Map();
          for (const ui of serverUi) next.set(ui.id, ui);
          prev.forEach((msg, key) => {
            const isPending =
              String(key).startsWith("temp-") ||
              msg.status === "sending" ||
              msg.status === "error";
            if (!isPending) return;
            if (personalPendingSupersededByServerList(serverUi, key, msg, userData)) return;
            if (!next.has(key)) next.set(key, msg);
          });
          return next;
        });
        setMessagesPageState({
          hasMore: false,
          lastMessageId: null,
          isInitialLoaded: true,
          isLoadingMore: false,
        });
        return;
      }

      const payload = await getConversationMessagesById(cid, {
        limit: 50,
        returnRaw: true,
      });
      const list = Array.isArray(payload.messages)
        ? payload.messages
        : Array.isArray(payload.data)
          ? payload.data
          : [];

      setMessagesMap((prev) => {
        const next = new Map();
        for (const msg of list) {
          next.set(msg.id, enrichMessageLocationFromApi(msg));
        }
        prev.forEach((msg, key) => {
          const isPending =
            String(key).startsWith("temp-") ||
            msg.status === "sending" ||
            msg.status === "error";
          if (!isPending) return;
          if (regularConversationPendingSupersededByServer(list, key, msg, userData)) return;
          if (!next.has(key)) next.set(key, msg);
        });
        return next;
      });

      const pagination = payload.pagination || {};
      setMessagesPageState({
        hasMore: Boolean(pagination.hasMore),
        lastMessageId: pagination.lastMessageId ?? null,
        isInitialLoaded: true,
        isLoadingMore: false,
      });
    } catch (err) {
      console.error("Erro ao recarregar mensagens da conversa:", err);
      const meta = getApiErrorMeta(err);
      setMessagesSyncError({
        code: meta.code,
        message:
          meta.message ||
          "Não foi possível carregar o histórico desta conversa. Tente novamente.",
        duplicates: meta.duplicates || [],
      });
      setMessagesPageState((prev) => ({
        ...prev,
        isLoadingMore: false,
        isInitialLoaded: true,
      }));
    }
  }, [selectedConversationId, userData]);

  const loadOlderMessages = async () => {
    if (!selectedConversationId) return;
    if (getPersonalChatKeyFromSyntheticConversationId(selectedConversationId)) return;
    if (messagesPageState.isLoadingMore || !messagesPageState.hasMore) return;

    const currentMessages = Array.from(messagesMap.values());
    if (!currentMessages.length) return;

    const sortedByIdAsc = [...currentMessages].sort((a, b) => {
      const idA = typeof a.id === "number" ? a.id : Number(a.id) || 0;
      const idB = typeof b.id === "number" ? b.id : Number(b.id) || 0;
      return idA - idB;
    });
    const oldest = sortedByIdAsc[0];
    if (!oldest?.id) return;

    setMessagesPageState((prev) => ({ ...prev, isLoadingMore: true }));

    try {
      const payload = await getConversationMessagesById(selectedConversationId, {
        limit: 50,
        beforeMessageId: oldest.id,
        returnRaw: true,
      });
      const list = Array.isArray(payload.messages)
        ? payload.messages
        : Array.isArray(payload.data)
          ? payload.data
          : [];

      setMessagesMap((prev) => {
        const next = new Map(prev);
        for (const msg of list) {
          if (!next.has(msg.id)) {
            next.set(msg.id, enrichMessageLocationFromApi(msg));
          }
        }
        return next;
      });

      const pagination = payload.pagination || {};
      setMessagesPageState((prev) => ({
        ...prev,
        hasMore: Boolean(pagination.hasMore),
        lastMessageId: pagination.lastMessageId ?? prev.lastMessageId,
        isLoadingMore: false,
      }));
    } catch (err) {
      console.error("Erro ao carregar mensagens antigas:", err);
      const meta = getApiErrorMeta(err);
      if (meta.code === "errors.clients.duplicate" && meta.duplicates.length) {
        setMessagesSyncError({
          code: meta.code,
          message:
            meta.message ||
            "Não foi possível carregar mensagens mais antigas por conflito de cadastro do cliente.",
          duplicates: meta.duplicates,
        });
      }
      setMessagesPageState((prev) => ({ ...prev, isLoadingMore: false }));
    }
  };

  const handleEndConversation = () => {
    alert("Conversa encerrada");
    setSelectedConversation(null);
    setMessagesMap(new Map());
    setMessagesSyncError(null);
    if (isMobile) {
      setShowConversationList(true);
      setShowMessageHistory(false);
    }
    navigate("/chat");
  };

  const handleDeleteMessage = useCallback(
    async (messageId) => {
      if (!selectedConversationId || messageId == null) return;
      if (getPersonalChatKeyFromSyntheticConversationId(selectedConversationId)) return;
      const mid = typeof messageId === "number" ? messageId : Number(messageId);
      if (!Number.isFinite(mid)) return;
      await deleteConversationMessage(selectedConversationId, mid);
      setMessagesMap((prev) => {
        const next = new Map(prev);
        next.delete(mid);
        return next;
      });
    },
    [selectedConversationId]
  );

  const containerStyle = {
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 1px 4px rgba(0, 0, 0, 0.5)"
        : "0 1px 4px rgba(0, 0, 0, 0.1)",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "8px",
    overflow: "hidden",
  };

  return {
    theme,
    isMobile,
    userData,
    selectedConversationId,
    selectedConversation,
    messages,
    showConversationList,
    showMessageHistory,
    showConversationDetails,
    isLoadingDetails,
    initialLoading,
    isLoadingConversation,
    messagesSyncError,
    reloadConversationMessages,
    searchTerm,
    filters,
    conversations,
    totalInDatabase,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    refetch,
    containerStyle,
    setSearchTerm,
    setFilters,
    setShowConversationList,
    setShowMessageHistory,
    setShowConversationDetails,
    setSelectedConversation,
    setMessages: setMessagesMap,
    handleSelectConversation,
    handleEndConversation,
    conversationDetails,
    refetchConversationDetails,
    sendMessageMutation,
    isError,
    createConversation,
    isCreatingConversation: isCreating,
    errorCreatingConversation: errorCreating,
    aiInsights,
    isLoadingInsights,
    aiInsightsError,
    loadAiInsights,
    hasMoreMessages: messagesPageState.hasMore,
    isLoadingMoreMessages: messagesPageState.isLoadingMore,
    loadOlderMessages,
    handleDeleteMessage,
    deletePersonalConversation,
  };
}
